import {
    Button,
    FieldGroup,
    Modal,
    TextButton,
    Typography,
    useAlert,
    useCalendar,
    useModal
} from "matsuri-ui"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useLocation } from "react-router-dom"
import { useMemo } from "react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import EditCalendarIcon from "@mui/icons-material/EditCalendar"
import UploadIcon from "@mui/icons-material/Upload"
import dayjs from "dayjs"
import { theme } from "../theme"
import {
    Shift,
    getShiftCurrentTime,
    isShiftTimeChangedByManager
} from "../model/shift"
import { FooterIconButtonAsLink } from "../components/FooterIconButtonAsLink"
import { ShiftForm } from "./ShiftPage/ShiftForm"
import {
    ShiftCalendarPageUI,
    useShiftContext
} from "./ShiftPage/ShiftCalendarPageUI"
import { useShift } from "@/hooks/useShifts"
import {
    requestDeleteShift,
    requestUnsubmitShift,
    requestUpdateShift
} from "@/hooks/useShift"
import { useTranslation } from "@/hooks/useTranslation"

// 本来はページにした方が作成機能とも一貫性があって良いが、現状はIDを指定してシフトを取ってくる機能がないので一旦モーダルで妥協する。
const EditShiftModal = ({
    shift,
    refetch,
    ...props
}: {
    shift: Shift
    refetch?: () => void
    onClose?: () => void
}) => {
    const { token } = useAuth()
    const { throwAlert } = useAlert()
    const { t } = useTranslation()

    return (
        <Modal
            {...props}
            backdrop
            header={<Typography variant="h3">{t("シフトの編集")}</Typography>}
            body={
                <FieldGroup
                    readOnly={
                        shift.status !== "Draft" && shift.status !== "Submitted"
                    }
                >
                    <ShiftForm
                        key={shift.id}
                        data={shift}
                        onSubmit={async input => {
                            const { error } = await requestUpdateShift(
                                token,
                                shift.id,
                                {
                                    ...input,
                                    status: shift.status
                                }
                            )
                            throwAlert(error, {
                                errorMessage: {
                                    happend: t("シフトの更新に失敗しました")
                                },
                                successMessage: t("シフトを更新しました"),
                                onSuccess: () => {
                                    props.onClose?.()

                                    refetch?.()
                                }
                            })
                        }}
                        onDelete={
                            // ここで分岐するの最悪なのでボタンのdisabledで制御した方がいい気がするが…？
                            shift.status === "Draft"
                                ? async () => {
                                      const ok = window.confirm(
                                          t(
                                              "この下書きを削除します。よろしいですか？"
                                          )
                                      )
                                      if (!ok) {
                                          return
                                      }

                                      const { error } =
                                          await requestDeleteShift(
                                              token,
                                              shift.id
                                          )
                                      throwAlert(error, {
                                          errorMessage: {
                                              happend:
                                                  t(
                                                      "シフトの削除に失敗しました"
                                                  )
                                          },
                                          successMessage:
                                              t("シフトを削除しました"),
                                          onSuccess: () => {
                                              props.onClose?.()

                                              refetch?.()
                                          }
                                      })
                                  }
                                : undefined
                        }
                        onUnsubmit={
                            shift.status === "Submitted"
                                ? async () => {
                                      const ok = window.confirm(
                                          t(
                                              "提出済みシフトを下書きに戻します。よろしいですか？"
                                          )
                                      )
                                      if (!ok) {
                                          return
                                      }

                                      const { error } =
                                          await requestUnsubmitShift(
                                              token,
                                              shift.id
                                          )
                                      throwAlert(error, {
                                          errorMessage: {
                                              happend:
                                                  t(
                                                      "シフトを下書きに戻せませんでした"
                                                  )
                                          },
                                          successMessage:
                                              t("シフトを下書きに戻しました"),
                                          onSuccess: () => {
                                              props.onClose?.()

                                              refetch?.()
                                          }
                                      })
                                  }
                                : undefined
                        }
                    />
                </FieldGroup>
            }
        />
    )
}

const ChangedChip = () => {
    const { t } = useTranslation()
    return (
        <Typography
            css={css`
                padding: 0 8px;
                font-size: 10px;
                line-height: 16px;
                background-color: ${theme.palette.intentions.cleanerGray.main};
                border-radius: 8px;
            `}
        >
            {t("変更あり")}
        </Typography>
    )
}

const RenderShiftItems = ({ date }: { date: dayjs.Dayjs }) => {
    const { shiftMap, selectedDate, refetch } = useShiftContext()
    const [openModal] = useModal(EditShiftModal)
    const { t } = useTranslation()

    return (
        <div
            css={css`
                display: grid;
                padding: 24px 20px;
            `}
        >
            <Typography variant="caption">
                {t(`${date.format("M月D日")}のシフト`)}
            </Typography>

            <div
                css={css`
                    & > * {
                        border-bottom: 1px solid
                            ${theme.palette.intentions.cleanerGray.main};
                    }

                    & > *:first-of-type {
                        padding-top: 0;
                    }

                    & > *:last-of-type {
                        padding-bottom: 0;
                        border-bottom: none;
                    }
                `}
            >
                {shiftMap?.[selectedDate.format("YYYYMMDD")]?.map(shift => (
                    <div
                        key={shift.id}
                        css={css`
                            display: grid;
                        `}
                    >
                        <button
                            css={css`
                                display: grid;
                                grid-template-columns: auto auto 1fr;
                                padding: 16px 0;
                                text-align: left;
                            `}
                            onClick={() => {
                                openModal({
                                    shift,
                                    refetch
                                })
                            }}
                        >
                            <div
                                css={[
                                    css`
                                        width: 6px;
                                        height: 100%;
                                        border-radius: 3px;
                                    `,
                                    shift.status === "Scheduled"
                                        ? css`
                                              background-color: ${
                                                  theme.palette.intentions
                                                      .primary.main
                                              };
                                          `
                                        : shift.status === "Cancelled"
                                          ? css`
                                                border: 1px solid
                                                    ${
                                                        theme.palette.intentions
                                                            .cleanerGray.main
                                                    };
                                            `
                                          : css`
                                                background-color: ${
                                                    theme.palette.intentions
                                                        .cleanerGray.main
                                                };
                                            `
                                ]}
                            />
                            <div
                                css={[
                                    css`
                                        display: grid;
                                        gap: 4px;
                                        padding-left: 8px;
                                    `,
                                    shift.status === "Cancelled" &&
                                        css`
                                            text-decoration: line-through;
                                        `
                                ]}
                            >
                                <Typography
                                    css={css`
                                        font-size: 16px;
                                        line-height: 1;
                                    `}
                                >
                                    {dayjs
                                        .unix(getShiftCurrentTime(shift).start)
                                        .format("HH:mm")}
                                    -
                                </Typography>
                                <Typography
                                    css={css`
                                        font-size: 12px;
                                        line-height: 16px;
                                    `}
                                >
                                    {dayjs
                                        .unix(getShiftCurrentTime(shift).end)
                                        .format("HH:mm")}
                                </Typography>
                            </div>

                            <div
                                css={css`
                                    display: grid;
                                    gap: 4px;
                                    padding-left: 16px;
                                `}
                            >
                                <Typography
                                    css={css`
                                        display: flex;
                                        gap: 8px;
                                        align-items: center;
                                        font-size: 16px;
                                        line-height: 1;
                                    `}
                                >
                                    {shift.status === "Draft"
                                        ? t("下書き")
                                        : shift.status === "Submitted"
                                          ? t("調整中")
                                          : shift.status === "Scheduled"
                                            ? t("確定")
                                            : shift.status === "Cancelled"
                                              ? t("キャンセル")
                                              : ""}
                                    {shift.status === "Scheduled" &&
                                    isShiftTimeChangedByManager(shift) ? (
                                        <ChangedChip />
                                    ) : null}
                                </Typography>
                                {shift.status === "Scheduled" &&
                                isShiftTimeChangedByManager(shift) ? (
                                    <Typography
                                        css={css`
                                            font-size: 12px;
                                            line-height: 16px;
                                        `}
                                    >
                                        (
                                        {dayjs
                                            .unix(shift.cleanerStart)
                                            .format("HH:mm")}
                                        -
                                        {dayjs
                                            .unix(shift.cleanerEnd)
                                            .format("HH:mm")}
                                        {t("からの変更)")}
                                    </Typography>
                                ) : null}
                            </div>
                        </button>
                    </div>
                )) ?? (
                    <Typography
                        css={css`
                            margin-top: 16px;
                            font-size: 16px;
                        `}
                    >
                        {t("シフトはありません")}
                    </Typography>
                )}
            </div>
        </div>
    )
}

export const ShiftPage = () => {
    const { t } = useTranslation()
    const { state } = useLocation()
    const stateTyped = state as { date?: string } | null
    const { date, increment, decrement, set } = useCalendar({
        initialDate: stateTyped?.date ? dayjs(stateTyped.date) : dayjs()
    })

    const { token } = useAuth()
    const startOfMonth = useMemo(() => date.startOf("month"), [date])
    const endOfMonth = useMemo(() => date.endOf("month"), [date])

    const { data: shifts, refetch } = useShift(token, startOfMonth, endOfMonth)

    return (
        <div>
            <ShiftCalendarPageUI
                date={date}
                shifts={shifts}
                refetch={refetch}
                onChangeDate={set}
                renderCalendarControls={() => (
                    <div
                        css={css`
                            display: flex;
                            justify-content: space-between;
                            padding: 0 16px;
                        `}
                    >
                        <div>
                            <p
                                css={css`
                                    font-size: 16px;
                                    font-weight: 700;
                                `}
                            >
                                {t(`${date.format("YYYY年M月")}`)}
                            </p>
                        </div>

                        <div
                            css={css`
                                display: flex;
                                gap: 16px;
                            `}
                        >
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => {
                                    set(dayjs())
                                }}
                            >
                                {t("今日")}
                            </Button>
                            <TextButton
                                onClick={decrement}
                                icon={<ArrowBackIcon />}
                            />
                            <TextButton
                                onClick={increment}
                                icon={<ArrowForwardIcon />}
                            />
                        </div>
                    </div>
                )}
                renderContent={RenderShiftItems}
                renderFooter={() => (
                    <div
                        css={css`
                            display: grid;
                            grid-template-columns: repeat(3, 1fr);
                            gap: 16px;
                        `}
                    >
                        <FooterIconButtonAsLink
                            to={`/`}
                            icon={<ArrowBackIosNewIcon />}
                        >
                            {t("清掃に戻る")}
                        </FooterIconButtonAsLink>
                        <FooterIconButtonAsLink
                            to={`/shift/new`}
                            icon={<EditCalendarIcon />}
                            variant="filled"
                            color="primary"
                            state={{ date: date.format("YYYY-MM-DD") }}
                        >
                            {t("下書きを追加")}
                        </FooterIconButtonAsLink>
                        <FooterIconButtonAsLink
                            to={`/shift/submit`}
                            icon={<UploadIcon />}
                            state={{ date: date.format("YYYY-MM-DD") }}
                        >
                            {t("提出に進む")}
                        </FooterIconButtonAsLink>
                    </div>
                )}
            />
        </div>
    )
}
